import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * HeroLevelPill — compact header chip showing the player's Hero Class
 * level (or class-pick prompt if none selected). Pulses gold when a
 * rebirth milestone (L10/20/30/40/50) is reached.
 */
export default function HeroLevelPill({ className = "" }) {
  const { isAuthed } = useAuth();
  const [hero, setHero] = useState(null);

  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    async function load() {
      try {
        const { data } = await axios.get(`${API}/hero-class/state`);
        if (cancelled) return;
        setHero(data);
      } catch (e) { /* ignore */ }
    }
    load();
    const iv = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [isAuthed]);

  if (!isAuthed || !hero) return null;

  // Class not picked yet — surface a "pick your class" call-to-action
  if (!hero.class_id) {
    return (
      <Link
        to="/hero-class"
        data-testid="hero-class-pick-pill"
        className={`flex items-center gap-1.5 px-2.5 py-1 border border-fuchsia-500/60 bg-fuchsia-900/30 hover:bg-fuchsia-800/40 transition-all ${className}`}
        style={{ borderRadius: 999 }}
        title="Pick your Hero Class"
      >
        <span className="text-base leading-none">🦸</span>
        <span className="text-[10px] font-black uppercase tracking-widest text-fuchsia-200">Pick Class</span>
      </Link>
    );
  }

  const level = int(hero.level);
  const rebirthCount = int(hero.rebirth_count);
  const canRebirth = !!hero.can_rebirth;
  const meta = hero.class_meta || {};
  const icon = meta.icon || "🦸";
  const className2 = meta.name || hero.class_id;

  return (
    <Link
      to="/hero-class"
      data-testid="hero-level-pill"
      className={`flex items-center gap-1.5 px-2.5 py-1 border transition-all ${
        canRebirth
          ? "border-yellow-400 bg-yellow-400/10 animate-pulse hover:bg-yellow-400/20"
          : "border-white/15 bg-black/40 hover:border-fuchsia-400/50"
      } ${className}`}
      style={{ borderRadius: 999 }}
      title={canRebirth ? "Rebirth ready! Tap to view your hero." : `${className2} · Lv ${level}`}
    >
      <span className="text-base leading-none">{icon}</span>
      <span className="text-[11px] font-bold tabular-nums text-white">
        <span className={canRebirth ? "text-yellow-300" : ""}>Lv {level}</span>
        {rebirthCount > 0 && (
          <span className="text-white/40 ml-0.5">·R{rebirthCount}</span>
        )}
      </span>
    </Link>
  );
}

function int(v) { return Number.parseInt(v) || 0; }
